// Confirmation drawer for removing a cart line (see cart.js's
// CartRemoveButton) - same gift-option-drawer shell as the PDP's
// engraving/gift-wrap popups. A single shared instance: opened with
// whichever line's Remove was clicked, "Remove" here confirms the removal,
// the [x]/overlay/"Move to wishlist" all just dismiss without removing.
class RemoveProductDialog extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-remove-product-modal]');
    this.overlay = this.querySelector('[data-remove-product-overlay]');
    this.pendingCartItems = null;
    this.pendingIndex = null;
    this.pendingEvent = null;
    this.triggerElement = null;

    if (!this.modal) return;

    // Query close/confirm triggers before relocating - relocateDrawer moves
    // the modal/overlay out to <body>, so afterwards they're no longer
    // descendants of `this` and these querySelectorAll/querySelector calls
    // would find nothing.
    const closeElements = this.querySelectorAll('[data-remove-product-close]');
    const wishlistButton = this.querySelector('[data-remove-product-wishlist]');
    this.confirmButton = this.querySelector('[data-remove-product-confirm]');

    this.relocateDrawer(this.modal, this.overlay);

    closeElements.forEach((element) => {
      element.addEventListener('click', () => this.close());
    });

    if (wishlistButton) {
      this.wishlistButton = wishlistButton;
      wishlistButton.addEventListener('click', () => this.moveToWishlist());
    }
    if (this.confirmButton) this.confirmButton.addEventListener('click', () => this.confirm());
  }

  open(cartItems, index, triggerElement, event) {
    this.pendingCartItems = cartItems;
    this.pendingIndex = index;
    this.pendingEvent = event;
    this.triggerElement = triggerElement;
    this.modal.show(triggerElement, triggerElement);
  }

  moveToWishlist() {
    if (!this.triggerElement) return;

    const productId = this.triggerElement.dataset.productId;
    const variantId = this.triggerElement.dataset.variantId;
    const customerId = this.triggerElement.dataset.customerId;

    if (!customerId) {
      window.location.href = '/account/login';
      return;
    }

    if (this.wishlistButton) {
      this.wishlistButton.classList.add('loading');
      this.wishlistButton.disabled = true;
      const spinner = this.wishlistButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    fetch('https://wishlistapp.binaryic.in/api/V1/wishlist/moveToProductWishlist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        shop_url: window.Shopify?.shop || window.location.hostname,
        customer_id: parseInt(customerId, 10),
        product_id: parseInt(productId, 10),
        variant_id: parseInt(variantId, 10)
      })
    })
    .then(response => response.json())
    .then(data => {
      // Increment the wishlist counter visually
      const wishlistCounters = document.querySelectorAll('.wkh-counter, .wishlist-count-bubble, .wishlist-count, [data-wishlist-count]');
      wishlistCounters.forEach(counter => {
        const currentCount = parseInt(counter.textContent.replace(/\D/g, ''), 10) || 0;
        counter.textContent = currentCount + 1;
        if (counter.classList.contains('hidden')) {
          counter.classList.remove('hidden');
        }
      });

      // Trigger the cart removal in the background
      if (this.pendingCartItems && this.pendingIndex) {
        this.pendingCartItems.updateQuantity(this.pendingIndex, 0, this.pendingEvent);
      }
      // Close the drawer immediately for better UX
      this.close();
    })
    .catch((error) => {
      console.error('Error moving to wishlist:', error);
    })
    .finally(() => {
      if (this.wishlistButton) {
        this.wishlistButton.classList.remove('loading');
        this.wishlistButton.disabled = false;
        const spinner = this.wishlistButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    });
  }

  // updateQuantity (cart.js) manages its own row-level loading spinner and
  // doesn't return a promise, so completion is tracked via the cartUpdate
  // event it publishes on success (matching the 'cart-items' source it
  // always uses) instead - keeps the button spinning for the customer
  // without needing to touch/duplicate that shared removal logic. The
  // safety timeout covers the error path, which cart.js surfaces via its
  // own error UI rather than a cartUpdate publish, so this button would
  // otherwise spin forever on a failed request.
  confirm() {
    if (!this.pendingCartItems || !this.pendingIndex) {
      this.close();
      return;
    }

    this.setLoading(true);

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      clearTimeout(safetyTimeout);
      this.setLoading(false);

      if (window.location.pathname === new URL(routes.cart_url, location.origin).pathname) {
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            const cartHeading = document.querySelector('#CartPageHeading');

            if (!cartHeading) return;

            cartHeading.textContent =
              cartHeading.textContent.trim().replace(/\d+/, cart.item_count);
          })
          .catch((error) => {
            console.error('Error updating cart count:', error);
          });
      }

      this.close();
    };

    const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') finish();
    });
    const safetyTimeout = setTimeout(finish, 8000);

    this.pendingCartItems.updateQuantity(this.pendingIndex, 0, this.pendingEvent);
  }

  setLoading(isLoading) {
    if (!this.confirmButton) return;
    this.confirmButton.classList.toggle('loading', isLoading);
    this.confirmButton.disabled = isLoading;
    const spinner = this.confirmButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  close() {
    this.pendingCartItems = null;
    this.pendingIndex = null;
    this.pendingEvent = null;
    if (this.modal) this.modal.hide();
  }

  // Same fixed-position relocation trick as gift-options.js's drawers - see
  // that file for why.
  relocateDrawer(drawer, overlay) {
    if (!drawer || drawer.parentElement === document.body) return;
    document.body.appendChild(drawer);
    if (overlay) drawer.after(overlay);
  }
}

if (!customElements.get('remove-product-dialog')) {
  customElements.define('remove-product-dialog', RemoveProductDialog);
}
